import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
} from "@tanstack/react-table";
import styles from "./styles.module.scss";
import { Breadcrumbs, Button } from "components";
import { PaginationTwo } from "components/pagination-two";
import { DoughnutChart } from "components/charts/doughnut";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Spinner } from "components/loader";
import { EmptyTable } from "components/empty-table";
import { emptyReport2, ChevronRightIcon } from "assets";
import { AssessCandidatesModal } from "components/modal-variants/assess-candidates-modal";
import { Routes } from "router";
import { useCVAnalysisCandidates, useConversationSessionDetail, CVAnalysisCandidate } from "hooks";

const SELECTED_CANDIDATES_KEY = "cv_analysis_selected_candidates";
const CANDIDATES_TO_INVITE_KEY = "cv_analysis_candidates_to_invite";
const PAGE_SIZE = 10;

const getScoreColors = (score: number): string[] => {
  if (score >= 90) return ["#18B368", "#18B36810"];
  if (score >= 70) return ["#1570EF", "#1570EF10"];
  if (score >= 50) return ["#EDA12F", "#EDA12F10"];
  return ["#E74444", "#E7444410"];
};

const columns: ColumnDef<CVAnalysisCandidate, unknown>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <div className="flex items-center justify-center gap-2">
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer"
        />
      </div>
    ),
    meta: {
      headerClassName: "w-[60px] px-3 py-4",
      cellClassName: "w-[60px] px-3 py-4 align-top",
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-center gap-2 pt-1">
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer"
        />
        <span className="text-sm text-[#667085]">{row.index + 1}</span>
      </div>
    ),
  },
  {
    id: "name",
    header: "Candidate Name",
    meta: {
      headerClassName: "px-4 py-4 text-left text-sm font-semibold text-[#1D2939]",
      cellClassName: "px-4 py-4 align-top",
    },
    cell: ({ row }) => (
      <>
        <div className="text-sm font-medium text-[#1D2939]">{row.original.name}</div>
        {row.original.email && (
          <div className="text-xs text-[#667085] mt-0.5">{row.original.email}</div>
        )}
      </>
    ),
  },
  {
    id: "strengths",
    header: "Key Strengths",
    meta: {
      headerClassName: "px-6 py-4 text-left text-sm font-semibold text-[#1D2939]",
      cellClassName: "px-6 py-4 align-top",
    },
    cell: ({ row }) => (
      <ul className="list-disc list-inside space-y-1">
        {row.original.key_strengths.map((strength, i) => (
          <li key={i} className="text-sm text-[#344054] leading-relaxed">
            {strength}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: "risks",
    header: "Risk/Gaps",
    meta: {
      headerClassName: "px-6 py-4 text-left text-sm font-semibold text-[#1D2939]",
      cellClassName: "px-6 py-4 align-top",
    },
    cell: ({ row }) => (
      <ul className="list-disc list-inside space-y-1">
        {row.original.risks_gaps.map((risk, i) => (
          <li key={i} className="text-sm text-[#344054] leading-relaxed">
            {risk}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: "fitScore",
    header: "Fit Score",
    meta: {
      headerClassName: "px-4 py-4 text-center text-sm font-semibold text-[#1D2939]",
      cellClassName: "px-4 py-4 align-top text-center",
    },
    cell: ({ row }) => {
      const numScore = parseFloat(row.original.fit_score);
      return (
        <div className="relative mx-auto" style={{ width: "56px", height: "56px" }}>
          <DoughnutChart
            score={numScore * 10}
            cutout="76%"
            color={getScoreColors(numScore * 10)}
          />
          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-[#344054] pointer-events-none">
            {numScore.toFixed(1)}
          </span>
        </div>
      );
    },
  },
  {
    id: "recommendation",
    header: "Recommendation",
    meta: {
      headerClassName: "px-6 py-4 text-left text-sm font-semibold text-[#1D2939]",
      cellClassName: "px-6 py-4 align-top",
    },
    cell: ({ row }) => (
      <span className="text-sm text-[#344054] leading-relaxed">
        {row.original.recommendation}
      </span>
    ),
  },
];

const ReviewTalentsCVAnalysisUI = () => {
  const { id: cvAnalysisId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const [showAssessModal, setShowAssessModal] = useState(false);

  const { candidates, isLoading } = useCVAnalysisCandidates({
    cvAnalysisId: cvAnalysisId || "",
  });

  // Restore selection from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(`${SELECTED_CANDIDATES_KEY}_${cvAnalysisId}`);
    if (stored) {
      try {
        const parsed: string[] = JSON.parse(stored);
        const initial: RowSelectionState = {};
        parsed.forEach((id) => { initial[id] = true; });
        setRowSelection(initial);
      } catch (e) {
        console.error("Failed to parse stored candidates:", e);
      }
    }
  }, [cvAnalysisId]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    setRowSelection((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (cvAnalysisId) {
        localStorage.setItem(
          `${SELECTED_CANDIDATES_KEY}_${cvAnalysisId}`,
          JSON.stringify(Object.keys(next).filter((k) => next[k]))
        );
      }
      return next;
    });
  };

  const table = useReactTable({
    data: candidates,
    columns,
    state: { rowSelection },
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
  });

  // Rows for the current page — shared by the desktop table and the mobile accordion
  // so both stay in sync with pagination.
  const pageRows = table.getRowModel().rows;

  const goToPage = (page: number) => {
    table.setPageIndex(page - 1);
    setExpandedIndex(0); // reset the mobile accordion when the page changes
  };

  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
  const firstSelectedCandidate = selectedIds.length > 0
    ? candidates.find((c) => c.id.toString() === selectedIds[0])
    : undefined;
  const assistantId = firstSelectedCandidate?.assessment ?? null;

  const { isFlow2 } = useConversationSessionDetail(assistantId);

  const selectedCandidatesData = candidates
    .filter((c) => rowSelection[c.id.toString()])
    .map((c) => ({ full_name: c.name, email: c.email }));

  const handleAssessCandidates = () => {
    if (selectedIds.length === 0) return;
    setShowAssessModal(true);
  };

  const handleAssessConfirm = () => {
    if (!assistantId) {
      setShowAssessModal(false);
      return;
    }

    localStorage.setItem(CANDIDATES_TO_INVITE_KEY, JSON.stringify(selectedCandidatesData));

    if (isFlow2) {
      localStorage.setItem("hiringAssistantFlowType", "flow_2");
      navigate(`${Routes.publishAssessment}?id2=${assistantId}&autoInvite=true`);
    } else {
      localStorage.setItem("hiringAssistantFlowType", "flow_1");
      navigate(`${Routes.publishAssessment}?id2=${assistantId}&autoAdminister=true`);
    }

    setShowAssessModal(false);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <section className={`md:px-9 px-4 md:py-5 py-6 ${styles.mainSection}`}>
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <section className={`md:px-9 px-4 md:py-5 py-6 ${styles.mainSection}`}>
        <Breadcrumbs />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6 mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-[#012169]">CV Analysis</h1>
            <p className="text-gray-500 mt-1">{candidates.length} candidates analyzed</p>
          </div>
          <Button
            type="fill"
            onClick={handleAssessCandidates}
            disabled={selectedIds.length === 0}
            className={`!px-6 !py-3 !bg-[#012169] !border-none hover:!bg-[#012169]/80 ${
              selectedIds.length === 0 ? "!opacity-50 !cursor-not-allowed" : ""
            }`}
          >
            Assess Candidates ({selectedIds.length})
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#EAECF0] overflow-hidden">
          {candidates.length === 0 ? (
            <EmptyTable
              image={emptyReport2}
              text="No candidates found for this CV analysis"
            />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-[1100px] w-full divide-y divide-[#EAECF0]">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className="bg-[#F9FAFB] divide-x divide-[#EAECF0]">
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={header.column.columnDef.meta?.headerClassName ?? ""}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-[#EAECF0]">
                    {pageRows.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`${
                          index % 2 !== 0 ? "bg-gray-50" : "bg-white"
                        } hover:bg-gray-50 transition-colors divide-x divide-[#EAECF0]`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={cell.column.columnDef.meta?.cellClassName ?? ""}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Accordion */}
              <div className="md:hidden space-y-4 p-4">
                {pageRows.map((row, index) => {
                  const candidate = row.original;
                  const numScore = parseFloat(candidate.fit_score);
                  const isSelected = row.getIsSelected();
                  return (
                    <div
                      key={candidate.id}
                      className="bg-white rounded-[12px] border border-gray-200 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                        className="!bg-[#EFF8FF] w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => row.toggleSelected()}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-[#D0D5DD] text-[#012169] focus:ring-[#012169] cursor-pointer"
                          />
                          <h3 className="text-lg font-medium text-[#344054]">{candidate.name}</h3>
                          <div className="relative" style={{ width: "40px", height: "40px" }}>
                            <DoughnutChart
                              score={numScore * 10}
                              cutout="76%"
                              color={getScoreColors(numScore * 10)}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-[#344054] pointer-events-none">
                              {numScore.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        {expandedIndex === index ? (
                          <ChevronRightIcon className="w-6 h-6 text-gray-400 -rotate-90 [&>path]:stroke-[#667085] [&>path]:stroke-[1px]" />
                        ) : (
                          <ChevronRightIcon className="w-6 h-6 text-gray-400 rotate-90 [&>path]:stroke-[#667085] [&>path]:stroke-[1px]" />
                        )}
                      </button>

                      {expandedIndex === index && (
                        <div className="px-6 py-5 bg-gray-50 border-t border-gray-200 space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Strengths</h4>
                            <ul className="space-y-2">
                              {candidate.key_strengths.map((strength, i) => (
                                <li key={i} className="text-gray-700 text-base leading-relaxed">
                                  • {strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Risk/Gaps</h4>
                            <ul className="space-y-2">
                              {candidate.risks_gaps.map((risk, i) => (
                                <li key={i} className="text-gray-700 text-base leading-relaxed">
                                  • {risk}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Recommendation</h4>
                            <p className="text-gray-700 text-base leading-relaxed">
                              {candidate.recommendation}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="px-4 py-3 border-t border-[#EAECF0]">
                <PaginationTwo
                  current={table.getState().pagination.pageIndex + 1}
                  total={Math.max(table.getPageCount(), 1)}
                  handleChange={goToPage}
                  count={candidates.length}
                  length={PAGE_SIZE}
                />
              </div>
            </>
          )}
        </div>
      </section>

      <AssessCandidatesModal
        isOpen={showAssessModal}
        onClose={() => setShowAssessModal(false)}
        onConfirm={handleAssessConfirm}
      />
    </div>
  );
};

export { ReviewTalentsCVAnalysisUI };
